/**
 * Sanitiser for the converted-PowerPoint HTML rendered by the `html` slide type.
 *
 * The fragment is injected into a Shadow DOM via innerHTML (see HtmlSlideCastingView /
 * HtmlSlideThumbnailPreview). Shadow DOM encapsulates STYLE, not script: inline event handlers
 * (`<img onerror>`, `<svg onload>`) and `javascript:` URLs still execute in the page's own origin.
 * Since the fragment is stored on the slide, an unsanitised inject is stored XSS hitting every
 * presenter / collaborator / capture-worker that renders it. So we run every fragment through
 * DOMPurify before it reaches a shadow root.
 *
 * The fragment is NOT ordinary rich text — it is a self-contained mini-document: the deck's shared
 * `<link rel="stylesheet">` + inline `<style>` scaffold, the slide's `<section class="aha-stage">`,
 * and the build markup the animation engine steps on (`data-step`, `data-aha-stagger`, the
 * `.aha-rise/.aha-fade/…` classes). DOMPurify keeps `class`, inline `style` and every `data-*`
 * attribute by default, so the layout and the build steps survive; we only widen the allow-list to
 * keep the stylesheet scaffold (`<link>`/`<style>` and their attributes), which the default HTML
 * profile drops. `on*` handlers and `javascript:`/`data:`-script URLs are stripped by DOMPurify's
 * defaults.
 */
import DOMPurify from 'dompurify'

const SANITIZE_CONFIG = {
  // The default HTML profile allows <style> but not <link>; keep both so the deck's stylesheet
  // scaffold reaches the shadow root. <section>, <img>, <svg>, <div>, <span> etc. are already
  // allowed by default.
  ADD_TAGS: ['link', 'style'],
  // <link>'s own attributes (rel/href/type/media) and <style>'s media are not in the default
  // attribute allow-list; class / style / data-* already are.
  ADD_ATTR: ['rel', 'href', 'type', 'media', 'crossorigin'],
  // Our fragment LEADS with the deck's <link>/<style> scaffold. Without this, DOMPurify parses those
  // leading metadata elements into <head> and drops them from the body output — FORCE_BODY keeps the
  // whole fragment (scaffold included) in one body context.
  FORCE_BODY: true,
  // Return an HTML string (not a DOM node) — the callers inject it via innerHTML.
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
}

/**
 * Strip active content (event handlers, `javascript:` URLs, `<script>`) from a converted-slide HTML
 * fragment while preserving its stylesheet scaffold, layout and build-step markup. Idempotent —
 * running it on already-clean HTML is a no-op, so it is safe to apply both at import time and again
 * at every render.
 * @param {string} html
 * @returns {string} sanitised HTML (empty string for empty/nullish input)
 */
// DOMPurify's CSS filter drops an entire <style> element when its rules carry certain comments — and
// the converter's shared scaffold <style> is exactly that. Losing it takes `.aha-abs .sh{position:
// absolute}` with it, so every absolutely-positioned converted shape collapses into normal flow and a
// heavy deck renders as one overflowing block. The scaffold is machine-generated CSS with no
// script-execution vector once injected into a shadow root, so lift each <style> out to a placeholder,
// sanitise the surrounding markup (where the real XSS surface is), then restore the styles verbatim in
// place. The match must open and close the block exactly where the HTML parser does — on `<style`/
// `</style` followed by whitespace, `/` or `>` — NOT on a literal `</style>`: the parser ends a style
// element at `</style ...>` (space/slash before the `>`), so a literal-only close lets a crafted
// `<style>x</style ><img onerror=…></style>` be hoisted whole and restored past DOMPurify while the
// browser then runs the smuggled handler. The lookaheads keep the regex and the parser in lock-step so
// everything after the real close still goes through DOMPurify.
export function sanitizeSlideHtml(html) {
  if (!html) return ''
  const styles = []
  const withSlots = html.replace(/<style(?=[\s/>])[\s\S]*?<\/style(?=[\s/>])[^>]*>/gi, (block) => {
    styles.push(block)
    return `<div data-aha-style-slot="${styles.length - 1}"></div>`
  })
  const clean = DOMPurify.sanitize(withSlots, SANITIZE_CONFIG)
  return clean.replace(/<div data-aha-style-slot="(\d+)"><\/div>/gi, (_, i) => styles[+i] || '')
}
